import { createContext, useContext, useEffect, useState } from "react";

import { useNavigate } from "react-router-dom";

import { PROJECT_STATUSES, STATEMENT_STATUSES } from "@/app/projects/constants";
import { useProjectPooling } from "@/hooks/use-project-pooling";
import getPath from "@/modules/get-path";
import {
  deleteTableItems,
  hasProjectFinished,
  isProjectProcessing,
} from "@/utils";

export const UnprocessedProjectContext = createContext();

export default function UnprocessedProjectProvider({
  children,
  project,
  statements,
  metadata,
}) {
  const navigate = useNavigate();
  const [isPooling, setIsPooling] = useState(isProjectProcessing(project));

  const pooling = useProjectPooling(
    getPath(project),
    project,
    statements,
    isPooling,
  );

  const isDisabled =
    isPooling ||
    isProjectProcessing(pooling.project) ||
    hasProjectFinished(pooling.project);

  const status = isPooling
    ? PROJECT_STATUSES.processing
    : pooling.project.status;

  useEffect(() => {
    if (hasProjectFinished(pooling.project) && isPooling) {
      setIsPooling(false);
      navigate(`${getPath(project)}?processed`);
    }
  }, [pooling.project, isPooling]);

  const deleteStatements = (statementIds) => {
    deleteTableItems("id", statementIds, {
      data: pooling.statements,
      setData: pooling.setStatements,
    });
  };

  const processStatements = (ids) => {
    const newStatements = pooling.statements.items.map((statement) => {
      if (ids.includes(statement.id)) {
        return { ...statement, status: STATEMENT_STATUSES.extracting };
      }
      return statement;
    });
    pooling.setProject({
      ...pooling.project,
      status: PROJECT_STATUSES.processing,
    });
    pooling.setStatements({ ...pooling.statements, items: newStatements });

    setTimeout(() => setIsPooling(true), 500);
  };

  return (
    <UnprocessedProjectContext.Provider
      value={{
        project,
        metadata,
        setIsPooling,
        isDisabled,
        pooling,
        status,
        isPooling,
        processStatements,
        deleteStatements,
      }}
    >
      {children}
    </UnprocessedProjectContext.Provider>
  );
}

export const useUnprocessedProjectProvider = () => {
  return useContext(UnprocessedProjectContext);
};
